import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { RouteSegments, DefaultRoutes, withOptionalDetailsId } from '@cbridge-packages/routing-client-admin';
import { ExternalRedirect } from '@cbridge-packages/routing';
import { ClientFeature } from '@cbridge-packages/auth';
import { DashboardsLayout, PricesLayout } from '@cbridge-fragments/dashboards';
import { ConfigLayout } from '@cbridge-fragments/config-container';
import { ProfilesScheduleLayout, ProfilesSpreadLayout, ProfilesRoutingLayout } from '@cbridge-fragments/config-profiles';
import { ReportingLayout } from '@cbridge-fragments/reporting';
import { RiskManagementLayout } from '@cbridge-fragments/risk-management';
import { StreamsLayout, AlertsLayout } from '@cbridge-fragments/config-settings';
import {
  SymbolsLpLayout,
  SymbolsLpDataLayout,
  SymbolsClientLayout,
  SymbolsClientDataLayout,
  SymbolsCoreLayout,
  SymbolsSyntheticLayout,
} from '@cbridge-fragments/config-symbols';
import { MarkupsLayout } from '@cbridge-fragments/config-markups';
import { ExternalReportingLayout } from '@cbridge-fragments/config-external-reporting';
import App from '../App';
import { SuspenseRoute } from './SuspenseRoute';
import { FeatureGuard } from './_components/FeatureGuard';
import { ProfilesIndexRedirect } from './_components/ProfilesIndexRedirect';

const ConnectionStatusRoute = lazy(() => import('../pages/dashboards/connectionStatus'));
const ExposureRoute = lazy(() => import('../pages/dashboards/exposure'));
const AlertsRoute = lazy(() => import('../pages/dashboards/alerts'));
const PerformanceMetricsRoute = lazy(() => import('../pages/dashboards/performanceMetrics'));
const ServerPricesRoute = lazy(() => import('../pages/dashboards/prices/serverPrices'));
const LpPricesRoute = lazy(() => import('../pages/dashboards/prices/lpPrices'));
const PricesEmptyRoute = lazy(() => import('../pages/dashboards/prices/pricesEmpty'));
const ReportingRoute = lazy(() => import('../pages/reporting/reporting'));
const ConversionRatesRoute = lazy(() => import('../pages/reporting/conversionRates'));
const MarkoutRoute = lazy(() => import('../pages/riskManagement/markout'));
const SettingsUsersRoute = lazy(() => import('../pages/config/settings/users'));
const SettingsServersRoute = lazy(() => import('../pages/config/settings/servers'));
const SettingsStreamsIndexRoute = lazy(() => import('../pages/config/settings/streams'));
const SettingsStreamsNodeRoute = lazy(() => import('../pages/config/settings/streams/nodeStreams'));
const SettingsLpsRoute = lazy(() => import('../pages/config/settings/lps'));
const AlertSettingsRoute = lazy(() => import('../pages/config/settings/alerts/settings'));
const AlertEmailsRoute = lazy(() => import('../pages/config/settings/alerts/emails'));
const AdjustmentRulesRoute = lazy(() => import('../pages/config/orderRouting/adjustment'));
const OrderLifetimeRulesRoute = lazy(() => import('../pages/config/orderRouting/orderLifetime'));
const OrderRoutingRulesRoute = lazy(() => import('../pages/config/orderRouting/orderRouting'));
const MarkupsIndexRoute = lazy(() => import('../pages/config/markups'));
const MarkupsProfileRoute = lazy(() => import('../pages/config/markups/profile'));
const CoreSymbolsRoute = lazy(() => import('../pages/config/symbols/coreSymbols'));
const SyntheticSymbolsRoute = lazy(() => import('../pages/config/symbols/syntheticSymbols'));
const ClientSymbolsIndexRoute = lazy(() => import('../pages/config/symbols/client'));
const NodeClientSymbolsRoute = lazy(() => import('../pages/config/symbols/client/nodeClientSymbols'));
const ServerClientSymbolsRoute = lazy(() => import('../pages/config/symbols/client/serverClientSymbols'));
const LpSymbolsIndexRoute = lazy(() => import('../pages/config/symbols/lp'));
const LpSymbolsRoute = lazy(() => import('../pages/config/symbols/lp/lpSymbols'));
const ProfilesScheduleRoute = lazy(() => import('../pages/config/profiles/scheduleProfiles'));
const ProfilesRoutingRoute = lazy(() => import('../pages/config/profiles/routingProfiles'));
const ProfilesOrderLifetimeRoute = lazy(() => import('../pages/config/profiles/orderLifetimeProfiles'));
const ProfilesAdjustmentRoute = lazy(() => import('../pages/config/profiles/adjustmentProfiles'));
const ProfilesPriceFilteringRoute = lazy(() => import('../pages/config/profiles/priceFilteringProfiles'));
const ProfilesPricingRoute = lazy(() => import('../pages/config/profiles/pricingProfiles'));
const ProfilesSpreadRoute = lazy(() => import('../pages/config/profiles/spreadProfiles'));
const PriceFilteringRulesRoute = lazy(() => import('../pages/config/pricingSettings/priceFilteringRules'));
const PricingRulesRoute = lazy(() => import('../pages/config/pricingSettings/pricingRules'));
const ExternalReportingReportsIndexRoute = lazy(() => import('../pages/config/externalReporting/reports'));
const ExternalReportingReportRulesRoute = lazy(() => import('../pages/config/externalReporting/reports/rules'));
const ExternalReportingServersRoute = lazy(() => import('../pages/config/externalReporting/servers'));
const NotFoundRoute = lazy(() => import('@cbridge-tools/not-found-page'));

export const Router = () => (
  <Routes>
    <Route element={<App />}>
      <Route index={true} element={<Navigate to={DefaultRoutes.Index} replace={true} />} />

      <Route path={RouteSegments.Root.Dashboard}>
        <Route element={<DashboardsLayout />}>
          <Route index={true} element={<Navigate to={RouteSegments.Dashboards.ConnectionStatus} replace={true} />} />
          <Route
            path={RouteSegments.Dashboards.ConnectionStatus}
            element={<SuspenseRoute element={<ConnectionStatusRoute />} />}
          />
          {/* Exposure is trading data: hidden without the basicBridge feature */}
          <Route element={<FeatureGuard feature={ClientFeature.BasicBridge} redirectTo={DefaultRoutes.Index} />}>
            <Route
              path={RouteSegments.Dashboards.Exposure}
              element={<SuspenseRoute element={<ExposureRoute />} />}
            />
          </Route>
          <Route
            path={RouteSegments.Dashboards.PerformanceMetrics}
            element={<SuspenseRoute element={<PerformanceMetricsRoute />} />}
          />
          <Route
            path={RouteSegments.Dashboards.Alerts}
            element={<SuspenseRoute element={<AlertsRoute />} />}
          />
        </Route>
        {/* Prices uses the config-style split layout (own sidebar drawer + ConfigPageLayout),
            so it is NOT nested under DashboardsLayout's MainContainer. */}
        <Route element={<FeatureGuard feature={ClientFeature.QuotesDashboard} redirectTo={`/${RouteSegments.Root.Dashboard}`} />}>
          <Route path={RouteSegments.Dashboards.Prices} element={<PricesLayout />}>
            <Route index={true} element={<SuspenseRoute element={<PricesEmptyRoute />} />} />
            <Route
              path={RouteSegments.DashboardPrices.Servers}
              element={<SuspenseRoute element={<ServerPricesRoute />} />}
            />
            <Route
              path={RouteSegments.DashboardPrices.Lps}
              element={<SuspenseRoute element={<LpPricesRoute />} />}
            />
          </Route>
        </Route>
      </Route>

      <Route path={RouteSegments.Root.Config} element={<ConfigLayout />}>
        <Route index={true} element={<Navigate to={DefaultRoutes.Config} replace={true} />} />

        {/* These routes are related to Core symbols */}
        <Route path={RouteSegments.Config.Symbols}>
          <Route
            index={true}
            element={
              <Navigate to={DefaultRoutes.ConfigSymbols} replace={true} />
            }
          />
          <Route path={RouteSegments.Symbols.Core} element={<SymbolsCoreLayout />}>
            <Route
              index={true}
              element={<SuspenseRoute element={<CoreSymbolsRoute />} />}
            />
            <Route
              path={RouteSegments.DetailsId}
              element={<SuspenseRoute element={<CoreSymbolsRoute />} />}
            />
          </Route>
          <Route path={RouteSegments.Symbols.Synthetic} element={<SymbolsSyntheticLayout />}>
            <Route
              index={true}
              element={<SuspenseRoute element={<SyntheticSymbolsRoute />} />}
            />
            <Route
              path={RouteSegments.DetailsId}
              element={<SuspenseRoute element={<SyntheticSymbolsRoute />} />}
            />
          </Route>
        </Route>

        {/* These routes are related to Client symbols */}
        <Route element={<SymbolsClientDataLayout />}>
          <Route element={<SymbolsClientLayout />}>
            <Route
              path={`${RouteSegments.Config.Symbols}/${RouteSegments.Symbols.Client}`}
              element={<SuspenseRoute element={<ClientSymbolsIndexRoute />} />}
            />
            <Route path={RouteSegments.Pools.Symbols}>
              <Route
                index={true}
                element={<SuspenseRoute element={<NodeClientSymbolsRoute />} />}
              />
              <Route
                path={RouteSegments.DetailsId}
                element={<SuspenseRoute element={<NodeClientSymbolsRoute />} />}
              />
            </Route>
            <Route path={RouteSegments.Servers.Symbols}>
              <Route
                index={true}
                element={<SuspenseRoute element={<ServerClientSymbolsRoute />} />}
              />
              <Route
                path={RouteSegments.DetailsId}
                element={<SuspenseRoute element={<ServerClientSymbolsRoute />} />}
              />
            </Route>
          </Route>
        </Route>

        {/* These routes are related to LP symbols */}
        <Route element={<SymbolsLpDataLayout />}>
          <Route element={<SymbolsLpLayout />}>
            <Route
              path={`${RouteSegments.Config.Symbols}/${RouteSegments.Symbols.LP}`}
              element={<SuspenseRoute element={<LpSymbolsIndexRoute />} />}
            />
            <Route
              path={withOptionalDetailsId(RouteSegments.Lps.Symbols)}
              element={<SuspenseRoute element={<LpSymbolsRoute />} />}
            />
          </Route>
        </Route>

        {/* Markups and Order Routing configure trading: hidden without the basicBridge feature */}
        <Route element={<FeatureGuard feature={ClientFeature.BasicBridge} redirectTo={DefaultRoutes.Config} />}>
          <Route path={RouteSegments.Config.Markups}>
            <Route index={true} element={<SuspenseRoute element={<MarkupsIndexRoute />} />} />
            <Route element={<MarkupsLayout />}>
              <Route
                path={withOptionalDetailsId(RouteSegments.Markups.Profile)}
                element={<SuspenseRoute element={<MarkupsProfileRoute />} />}
              />
            </Route>
          </Route>

          <Route path={RouteSegments.Config.OrderRouting}>
            <Route
              index={true}
              element={<Navigate to={DefaultRoutes.ConfigOrderRouting} replace={true} />}
            />
          </Route>
        </Route>

        <Route path={RouteSegments.Config.PricingSettings}>
          <Route
            index={true}
            element={<Navigate to={DefaultRoutes.ConfigPricingRules} replace={true} />}
          />
        </Route>

        <Route path={RouteSegments.Config.Rules}>
          <Route element={<FeatureGuard feature={ClientFeature.BasicBridge} redirectTo={DefaultRoutes.Config} />}>
            <Route
              path={withOptionalDetailsId(RouteSegments.Rules.OrderRouting)}
              element={<SuspenseRoute element={<OrderRoutingRulesRoute />} />}
            />
            <Route
              path={withOptionalDetailsId(RouteSegments.Rules.OrderLifetime)}
              element={<SuspenseRoute element={<OrderLifetimeRulesRoute />} />}
            />
            <Route
              path={withOptionalDetailsId(RouteSegments.Rules.Adjustment)}
              element={<SuspenseRoute element={<AdjustmentRulesRoute />} />}
            />
          </Route>
          <Route
            path={withOptionalDetailsId(RouteSegments.Rules.PricingRules)}
            element={<SuspenseRoute element={<PricingRulesRoute />} />}
          />
          {/* Price filtering (depth) is not needed for markout: hidden without the basicBridge feature */}
          <Route element={<FeatureGuard feature={ClientFeature.BasicBridge} redirectTo={DefaultRoutes.ConfigPricingRules} />}>
            <Route
              path={withOptionalDetailsId(RouteSegments.Rules.PriceFilteringRules)}
              element={<SuspenseRoute element={<PriceFilteringRulesRoute />} />}
            />
          </Route>
        </Route>

        <Route path={RouteSegments.Config.Profiles}>
          <Route index={true} element={<ProfilesIndexRedirect />} />
          <Route
            path={withOptionalDetailsId(RouteSegments.Profiles.Pricing)}
            element={<SuspenseRoute element={<ProfilesPricingRoute />} />}
          />
          {/* Only pricing profiles matter without the basicBridge feature; the rest configure trading */}
          <Route element={<FeatureGuard feature={ClientFeature.BasicBridge} redirectTo={DefaultRoutes.ConfigProfilesPricing} />}>
            <Route path={RouteSegments.Profiles.Schedule} element={<ProfilesScheduleLayout />}>
              <Route
                index={true}
                element={<SuspenseRoute element={<ProfilesScheduleRoute />} />}
              />
              <Route
                path={RouteSegments.DetailsId}
                element={<SuspenseRoute element={<ProfilesScheduleRoute />} />}
              />
              <Route
                path={RouteSegments.Schedule.Interval}
                element={<SuspenseRoute element={<ProfilesScheduleRoute />} />}
              />
            </Route>

            <Route path={RouteSegments.Profiles.Routing} element={<ProfilesRoutingLayout />}>
              <Route
                index={true}
                element={<SuspenseRoute element={<ProfilesRoutingRoute />} />}
              />
              <Route
                path={RouteSegments.DetailsId}
                element={<SuspenseRoute element={<ProfilesRoutingRoute />} />}
              />
            </Route>
            <Route
              path={withOptionalDetailsId(RouteSegments.Profiles.OrderLifetime)}
              element={<SuspenseRoute element={<ProfilesOrderLifetimeRoute />} />}
            />
            <Route
              path={withOptionalDetailsId(RouteSegments.Profiles.Adjustment)}
              element={<SuspenseRoute element={<ProfilesAdjustmentRoute />} />}
            />
            <Route
              path={withOptionalDetailsId(RouteSegments.Profiles.PriceFiltering)}
              element={<SuspenseRoute element={<ProfilesPriceFilteringRoute />} />}
            />
            <Route
              path={withOptionalDetailsId(RouteSegments.Profiles.Spread)}
              element={<ProfilesSpreadLayout />}
            >
              <Route index={true} element={<SuspenseRoute element={<ProfilesSpreadRoute />} />} />
            </Route>
          </Route>
        </Route>

        <Route path={RouteSegments.Config.Settings}>
          <Route
            index={true}
            element={<Navigate to={DefaultRoutes.ConfigSettings} replace={true} />}
          />
        </Route>

        <Route
          path={RouteSegments.Settings.Users}
          element={<SuspenseRoute element={<SettingsUsersRoute />} />}
        />

        <Route path={RouteSegments.Settings.Streams}>
          <Route index={true} element={<SuspenseRoute element={<SettingsStreamsIndexRoute />} />} />
        </Route>

        <Route element={<StreamsLayout />}>
          <Route
            path={withOptionalDetailsId(RouteSegments.Pools.Streams)}
            element={<SuspenseRoute element={<SettingsStreamsNodeRoute />} />}
          />
        </Route>

        <Route
          path={withOptionalDetailsId(RouteSegments.Settings.Servers)}
          element={<SuspenseRoute element={<SettingsServersRoute />} />}
        />

        <Route
          path={withOptionalDetailsId(RouteSegments.Settings.LiquidityProviders)}
          element={<SuspenseRoute element={<SettingsLpsRoute />} />}
        />

        <Route path={RouteSegments.Config.Alerts} element={<AlertsLayout />}>
          <Route
            index={true}
            element={<Navigate to={RouteSegments.Alerts.Settings} />}
          />
          <Route
            path={RouteSegments.Alerts.Settings}
            element={<SuspenseRoute element={<AlertSettingsRoute />} />}
          />
          <Route
            path={RouteSegments.Alerts.Emails}
            element={<SuspenseRoute element={<AlertEmailsRoute />} />}
          />
        </Route>

        {/* External reporting is coverage, i.e. trading: needs basicBridge on top of its own feature */}
        <Route element={<FeatureGuard feature={ClientFeature.BasicBridge} redirectTo={DefaultRoutes.Config} />}>
          <Route element={<FeatureGuard feature={ClientFeature.ExternalReporting} redirectTo={DefaultRoutes.Config} />}>
            <Route path={RouteSegments.Config.ExternalReporting} element={<ExternalReportingLayout />}>
              <Route index={true} element={<Navigate to={DefaultRoutes.ConfigExternalReporting} replace={true} />} />
              <Route path={RouteSegments.ExternalReporting.Reports}>
                <Route index={true} element={<SuspenseRoute element={<ExternalReportingReportsIndexRoute />} />} />
                <Route
                  path={withOptionalDetailsId(RouteSegments.ExternalReportingRuleSetId)}
                  element={<SuspenseRoute element={<ExternalReportingReportRulesRoute />} />}
                />
              </Route>
              <Route
                path={withOptionalDetailsId(RouteSegments.ExternalReporting.Servers)}
                element={<SuspenseRoute element={<ExternalReportingServersRoute />} />}
              />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Reports are trading data: hidden without the basicBridge feature */}
      <Route element={<FeatureGuard feature={ClientFeature.BasicBridge} redirectTo={DefaultRoutes.Index} />}>
        <Route path={RouteSegments.Root.Reporting} element={<ReportingLayout />}>
          <Route index={true} element={<Navigate to={RouteSegments.Reporting.Executions} />} />
          <Route
            path={RouteSegments.Reporting.Executions}
            element={<SuspenseRoute element={<ReportingRoute type={RouteSegments.Reporting.Executions} />} />}
          />
          <Route
            path={RouteSegments.Reporting.Exposure}
            element={<SuspenseRoute element={<ReportingRoute type={RouteSegments.Reporting.Exposure} />} />}
          />
          <Route
            path={RouteSegments.Reporting.Orders}
            element={<SuspenseRoute element={<ReportingRoute type={RouteSegments.Reporting.Orders} />} />}
          />
          <Route
            path={RouteSegments.Reporting.TradingVolume}
            element={<SuspenseRoute element={<ReportingRoute type={RouteSegments.Reporting.TradingVolume} />} />}
          />
          <Route
            path={RouteSegments.Reporting.ConversionRates}
            element={<SuspenseRoute element={<ConversionRatesRoute />} />}
          />
        </Route>
      </Route>

      {/* Markout report: the markoutReport feature, or riskManagement (independent markout) */}
      <Route
        path={RouteSegments.Root.RiskManagement}
        element={<FeatureGuard feature={[ClientFeature.MarkoutReport, ClientFeature.RiskManagement]} redirectTo={DefaultRoutes.Index} />}
      >
        <Route element={<RiskManagementLayout />}>
          <Route index={true} element={<Navigate to={RouteSegments.RiskManagement.Markout} replace={true} />} />
          <Route path={RouteSegments.RiskManagement.Markout}>
            <Route index={true} element={<SuspenseRoute element={<MarkoutRoute />} />} />
            {/* A report URL without a job id redirects to the report's latest job */}
            <Route path={':reportId'} element={<SuspenseRoute element={<MarkoutRoute />} />} />
            <Route
              path={':reportId/jobs/:jobId'}
              element={<SuspenseRoute element={<MarkoutRoute />} />}
            />
          </Route>
        </Route>
      </Route>

      <Route path={RouteSegments.Root.Goto}>
        <Route path={RouteSegments.Goto.Help} element={<ExternalRedirect to={RouteSegments.Help} />} />
      </Route>

      {/* 404 - Not Found Route - Must be the last route */}
      <Route path={'*'} element={<SuspenseRoute element={<NotFoundRoute />} />} />
    </Route>
  </Routes>
);
